<template>
  <!-- O gatilho é um botão COM CARA DE CAMPO, e não um campo de verdade. Com um campo na barra,
       digitar aconteceria em dois lugares — ali e na paleta — e os dois valores teriam de ficar
       em sincronia a cada tecla. Um input só, dentro da paleta, e o da barra é a porta.

       No xs só o ícone: ali a barra já trocou as abas de coleção por um seletor, e não há
       largura para a palavra. -->
  <v-btn v-if="$vuetify.display.xs" variant="text" size="small" icon="mdi-magnify"
         :aria-label="$tt('search.open')" @click="open()"/>
  <v-btn v-else variant="text" class="shell-trigger shell-plain-case" @click="open()">
    <v-icon start size="small">mdi-magnify</v-icon>
    <span class="search-trigger-text">
      <!-- `shell-value` é a tipografia da barra: com ela a palavra encolhe junto com o nome do
           ambiente e as abas nas telas estreitas, em vez de ter um tamanho só dela. -->
      <span class="shell-value">{{ $tt('search.open') }}</span>
      <!-- O atalho escrito no próprio gatilho: é como alguém descobre que ele existe. EMBAIXO da
           palavra, como ficava o selo do modo embaixo do logo (pedido do usuário), e com a mesma
           tecla desenhada de quando ficava ao lado — só menor, para as duas linhas caberem no
           botão. Some no que é estreito, porque ali o que importa é a palavra. -->
      <span v-if="$vuetify.display.mdAndUp" class="search-trigger-keys">{{ keys }}</span>
    </span>
  </v-btn>

  <!-- O foco vai ao campo quando a animação TERMINA, e não só pelo `autofocus`: o conteúdo do
       diálogo monta junto com a transição, e focar no meio dela às vezes não pega — e uma paleta
       que abre sem o cursor dentro perde justamente o que ela promete, que é digitar na hora. -->
  <v-dialog v-model="asking" :max-width="720" :fullscreen="$vuetify.display.smAndDown"
            scrollable @after-enter="focus()" @after-leave="forget()">
    <v-card class="search-palette">
      <!-- O campo tem a largura do cartão e nenhuma moldura: a paleta inteira É o campo, e um
           contorno dentro de outro contorno só acrescenta linha. -->
      <div class="search-field">
        <v-text-field ref="field" v-model="text" autofocus clearable hide-details variant="plain"
                      density="comfortable" prepend-inner-icon="mdi-magnify"
                      :placeholder="$tt('search.placeholder')"
                      @keydown.down.prevent="move(1)"
                      @keydown.up.prevent="move(-1)"
                      @keydown.enter.prevent="openActive($event)"/>
      </div>
      <!-- Dois pixels, e sempre no mesmo lugar: a barra de progresso no meio do conteúdo
           empurraria a lista a cada tecla. -->
      <v-progress-linear :active="searching" indeterminate color="primary" height="2"/>

      <div ref="list" class="search-results">
        <div v-if="short" class="search-note">{{ $tt('search.min-chars', {count: MIN}) }}</div>
        <div v-else-if="!sections.length && !searching" class="search-note">
          {{ $tt('search.none', {text: needle}) }}
        </div>
        <v-list v-else density="compact" class="py-0">
          <template v-for="section of sections" :key="section.idea">
            <v-list-subheader class="search-group">
              <span class="search-group-name">{{ section.name }}</span>
              <!-- `(5+18)`: o mesmo par "à vista + ocultos" da faixa do Kanban e do botão de
                   raias. O segundo número é o que NÃO caberia na paleta, e é ele que convida a
                   escrever uma letra a mais em vez de rolar. -->
              <span class="search-group-count">
                <ontos-count :count="section.shown" :hidden="section.hidden"/>
              </span>
            </v-list-subheader>

            <!-- `<a>` com href de verdade, e não router-link: é o que faz ctrl+clique e botão
                 do meio abrirem a página inteira em outra aba. A decisão de deixar o browser
                 agir mora no `opensByItself`, que as telas de item já usam. -->
            <v-list-item v-for="row of section.rows" :key="row.index"
                         :href="row.href" :active="row.index === active"
                         :data-row="row.index"
                         @click="pick($event, row)"
                         @mouseenter="active = row.index">
              <div class="search-title">
                <!-- A régua à esquerda do nome, com a cor derivada do id: é assim que o app diz
                     "isto é um ITEM de coleção", e é a forma `accent` do `ValueForm`. De pé e
                     não colada na borda porque isto é um item de LISTA e não célula de tabela —
                     e porque dentro de um `v-list-item` a régua absoluta seria cortada pelo
                     `overflow: hidden` do conteúdo.

                     A situação ao lado, na bolinha, que é a forma dela. Duas naturezas na mesma
                     linha, cada uma na sua marca. -->
                <ontos-colored-value :option="row.option" form="accent" class="search-identifier"/>
                <ontos-colored-value v-if="row.state" :option="row.state" form="dot"
                                     class="search-state"/>
              </div>
              <!-- Por que este item apareceu. Sem isto, três itens de nome parecido viram uma
                   lista em que não se sabe qual escolher. -->
              <div v-if="row.hit" class="search-why">
                <v-icon size="x-small" class="search-why-icon">{{ row.hit.icon }}</v-icon>
                <span class="search-why-field">{{ row.hit.name }}</span>
                <span class="search-why-sep">·</span>
                <!-- O valor, na forma que a natureza dele pede: o chip tingido de uma opção de
                     domínio, a data no formato de quem lê, e o trecho com realce para o resto. -->
                <ontos-colored-value v-if="row.hit.option" :option="row.hit.option" form="chip"/>
                <span v-else-if="row.hit.plain" class="search-why-text">{{ row.hit.plain }}</span>
                <span v-else class="search-why-text">{{ row.hit.before }}<mark>{{ row.hit.match }}</mark>{{ row.hit.after }}</span>
              </div>
            </v-list-item>
          </template>
        </v-list>
      </div>

      <div v-if="!short" class="search-hints">{{ $tt('search.hints') }}</div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch, toNative} from 'vue-facing-decorator'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import Count from '@/components/shared/Count.vue'
import Idea from '@/types/Idea'
import {ColoredOption} from '@/types/ColoredOption'
import {InstanceContext} from '@/types/InstanceContext'
import {EnhancedWorkpace} from '@/types/Workspace'
import {MIN_SEARCH_TEXT, SearchItem, SearchResult} from '@/services/SearchService'
import {instanceRoute, opensByItself, resolveInstanceHref} from '@/utils/instanceLink'
import {titleOfInstance} from '@/utils/ontosUtils'
import {choiceOption, instanceColorOf, valueText} from '@/utils/propertyDisplay'
import {typeIcon} from '@/utils/propertyDefinition'

/** Uma linha de resultado, pronta para desenhar — e com o índice que o teclado percorre. */
type Row = {
  index: number
  href: string
  context: InstanceContext
  /** O item como valor colorido: o id, o nome composto e a cor derivada do id. */
  option: ColoredOption
  state?: ColoredOption
  hit?: Why
}

/**
 * Por que a linha apareceu: o campo, e o valor na forma que a natureza dele pede.
 *
 * As três formas são exclusivas, e a escolha entre elas é a mesma pergunta que o `ValueForm` já
 * responde no resto do app: `option` é opção de domínio (chip tingido), `plain` é valor que o
 * app nunca mostra cru — data —, e o trio `before`/`match`/`after` é o trecho de texto com o
 * realce no que casou.
 *
 * **Ausente quando o campo compõe a chave**: ali o identificador da linha de cima já É o campo,
 * e a linha de apoio não teria o que acrescentar. Ausente também quando o item veio pelo próprio
 * número, e quando o campo saiu do desenho depois da leitura.
 */
type Why = {
  icon: string
  name: string
  option?: ColoredOption
  plain?: string
  before?: string
  match?: string
  after?: string
}

/** Um grupo: a coleção, os acertos que couberam e quantos ficaram de fora. */
type Section = { idea: string, name: string, shown: number, hidden: number, rows: Row[] }

/**
 * Buscar por texto em qualquer item do ambiente, de qualquer tela.
 *
 * **Três coisas num componente só**: o gatilho na barra, o atalho de teclado e a paleta. Separá-
 * los faria o `keydown` de `Ctrl+K` morar longe do que ele abre — e o primeiro atalho global do
 * app merece estar ao lado do que justifica a sua existência.
 *
 * Mora na barra, dentro da condição de ambiente: sem ambiente não há onde procurar. Nas rotas
 * públicas a barra não é desenhada, e com ela o atalho também não — que é o certo, porque no
 * login não há o que buscar.
 *
 * **A espera antes de buscar é daqui**, e não do servidor: cada tecla é uma requisição que
 * atravessa todas as coleções do ambiente, e só a última interessa. Junto com ela vem o
 * descarte da resposta atrasada — sem isso a lista pisca com o resultado da tecla anterior.
 */
const DELAY = 250

@Component({
  name: 'ontos-global-search',
  emits: ['showErrorMessage'],
  components: {'ontos-colored-value': ColoredValue, 'ontos-count': Count}
})
class GlobalSearch extends Vue {
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace

  asking = false
  text = ''
  searching = false
  /** A linha que o teclado aponta. Zero é a primeira: `↵` sem mexer nas setas abre o melhor acerto. */
  active = 0
  private result: SearchResult | null = null
  private timer: any = null
  /**
   * O número do pedido em curso. É o que distingue "a resposta que chegou" de "a resposta que
   * interessa": digitar depressa manda várias, e elas não voltam necessariamente em ordem.
   */
  private asked = 0

  //Exposto ao template: constante de módulo não é enxergada de lá.
  MIN = MIN_SEARCH_TEXT

  mounted() {
    window.addEventListener('keydown', this.onKey)
  }

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKey)
    this.stopWaiting()
  }

  /**
   * `Ctrl+K` — `⌘K` no Mac —, que é o atalho que todo mundo já tem no dedo.
   *
   * No `window` porque ele vale de qualquer tela e sem nenhum clique antes. O `preventDefault`
   * é o que impede o navegador de abrir a barra de endereço dele no mesmo gesto.
   */
  private onKey(event: KeyboardEvent) {
    if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'k') return
    event.preventDefault()
    this.open()
  }

  get keys(): string {
    return this.apple ? '⌘K' : 'Ctrl K'
  }

  /** O rótulo do atalho muda com o teclado de quem olha; a tecla que o código escuta, não. */
  private get apple(): boolean {
    return /mac|iphone|ipad/i.test(navigator.userAgent)
  }

  open() {
    this.asking = true
  }

  focus() {
    const field = this.$refs.field as { focus?: () => void } | undefined
    if (field && field.focus) field.focus()
  }

  /**
   * Fecha e esquece — depois da animação, para o conteúdo não sumir na frente de quem olha.
   *
   * Esquecer é de propósito: a paleta abre limpa. Guardar o texto faria a busca de ontem
   * reaparecer como se fosse a de agora, e o custo de digitar três letras é menor que o de
   * desconfiar do que está na tela.
   */
  forget() {
    this.text = ''
    this.result = null
    this.active = 0
    this.stopWaiting()
  }

  get needle(): string {
    return (this.text || '').trim()
  }

  get short(): boolean {
    return this.needle.length < MIN_SEARCH_TEXT
  }

  @Watch('text')
  onText() {
    this.stopWaiting()
    if (this.short) {
      //Apagar o que se digitou limpa a lista na hora: resultado de um texto que já não está no
      //campo é resposta a uma pergunta que ninguém fez mais.
      this.result = null
      this.asked++
      this.searching = false
      return
    }
    this.searching = true
    this.timer = setTimeout(() => this.find(), DELAY)
  }

  private stopWaiting() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
  }

  private async find() {
    const needle = this.needle
    const ticket = ++this.asked
    try {
      const result = await this.$search.find({systemAlias: this.system}, needle)
      //Chegou atrasada: outra tecla já pediu outra coisa, e esta resposta é história.
      if (ticket !== this.asked) return
      this.result = result
      this.active = 0
    } catch (err: any) {
      if (ticket === this.asked) this.$emit('showErrorMessage', err.message)
    } finally {
      if (ticket === this.asked) this.searching = false
    }
  }

  private get system(): string {
    return this.$route.params.system as string
  }

  /**
   * Os grupos como a tela os desenha: na ordem em que o servidor os mandou, que é a ordem das
   * abas do ambiente.
   *
   * Monta tudo aqui, e não no template: cada linha precisa do identificador, da situação, do
   * ícone do campo e do trecho partido em três, e resolver isso em expressões de template
   * significaria procurar a coleção e a propriedade de novo a cada redesenho.
   *
   * **Plural sempre**, mesmo no design: o grupo é um punhado de ITENS daquela coleção.
   */
  get sections(): Section[] {
    let index = 0
    const sections: Section[] = []
    for (const group of (this.result && this.result.groups) || []) {
      //Coleção que saiu do desenho enquanto a paleta estava aberta: sem ela não há como nomear
      //o grupo nem o campo que casou, e um grupo sem nome é pior que um grupo a menos.
      const idea = this.workspace.ideaWith(group.idea)
      if (!idea) continue
      const workflow = this.workspace.workflowOf(idea)
      const rows = group.items.map(item => {
        const context = {system: this.system, idea: idea.alias, instance: String(item.instance)}
        return {
          index: index++,
          context,
          href: resolveInstanceHref(this.$router, context),
          option: {
            id: item.instance,
            //Pelo `titleOfInstance`, que é o dono da regra: item sem chave preenchida sai aqui
            //como sai na listagem e na ficha — "Demanda #42", e não em branco
            name: titleOfInstance({_identifier: item.identifier, _instance: item.instance}, idea),
            color: instanceColorOf(item.instance)
          },
          state: workflow ? workflow.coloredStateOf({_state: item.state}) : undefined,
          hit: this.hitOf(idea, item)
        }
      })
      sections.push({
        idea: idea.alias, name: idea.pluralName, rows,
        shown: rows.length, hidden: Math.max(0, group.count - rows.length)
      })
    }
    return sections
  }

  private get rows(): Row[] {
    return this.sections.flatMap(section => section.rows)
  }

  /**
   * O acerto, pronto para ler: o ícone do tipo do campo, o nome dele e o trecho partido no
   * casamento.
   *
   * **Quem partiu o texto foi o servidor**, que manda a posição junto: é ele quem sabe o que
   * casou, porque o padrão ignora acento e caixa. Procurar o texto digitado aqui seria a
   * segunda cópia dessa regra, em outra linguagem.
   */
  private hitOf(idea: Idea, item: SearchItem): Why | undefined {
    const hit = item.hit
    if (!hit) return undefined
    //Campo que saiu do desenho desde a leitura: sem ele não há nome nem ícone a mostrar
    const property = idea.properties.find(candidate => candidate.alias === hit.property)
    if (!property) return undefined
    //Campo da CHAVE: a linha de baixo não aparece. O identificador acima JÁ é esse campo — ele
    //é composto exatamente dos atributos marcados —, então nomeá-lo seria dizer duas vezes a
    //mesma coisa, e com a segunda vez mais apagada que a primeira.
    if (property.identifier) return undefined
    const where = {icon: typeIcon(property), name: property.name}
    //Opção de domínio: o chip com a cor dela, que é a forma que ela tem em toda outra tela — a
    //listagem, o card do Kanban, a ficha. Sem realce dentro do chip: a cor já é a resposta, e o
    //que casou é o nome inteiro da opção.
    if (property.type === 'choice')
      return {...where, option: choiceOption(property, hit.value, this.workspace.domains)}
    //O que o app nunca mostra cru — a data, gravada como 'YYYY-MM-DD'. Pelo `valueText`, que é o
    //dono de "texto de um valor". Sem realce de propósito: a posição do casamento é a do texto
    //GRAVADO, e pintá-la sobre o texto formatado marcaria o pedaço errado.
    if (hit.value !== undefined) return {...where, plain: valueText(property, hit.value)}

    return {
      ...where,
      before: hit.text.slice(0, hit.at),
      match: hit.text.slice(hit.at, hit.at + hit.length),
      after: hit.text.slice(hit.at + hit.length)
    }
  }

  /** As setas dão a volta: numa lista curta, descer no fim é mais rápido que subir tudo. */
  move(delta: number) {
    const total = this.rows.length
    if (!total) return
    this.active = (this.active + delta + total) % total
    this.$nextTick(() => this.showActive())
  }

  private showActive() {
    const list = this.$refs.list as HTMLElement | undefined
    const row = list && list.querySelector(`[data-row="${this.active}"]`)
    if (row) (row as HTMLElement).scrollIntoView({block: 'nearest'})
  }

  openActive(event: KeyboardEvent) {
    const row = this.rows[this.active]
    if (!row) return
    //Com modificador, o mesmo que no clique: abre em outra aba e a paleta fica
    if (event.ctrlKey || event.metaKey) window.open(row.href, '_blank')
    else this.go(row.context)
  }

  /**
   * O `v-list-item` dispara `click` também pelo TECLADO, e por isso o evento pode ser um dos
   * dois. A pergunta "quem abre é o browser?" é a mesma nos dois casos, e o `opensByItself` a
   * responde por forma e não por classe de evento — o botão do mouse é o que o teclado não tem.
   */
  pick(event: MouseEvent | KeyboardEvent, row: Row) {
    if (opensByItself(event)) return
    event.preventDefault()
    this.go(row.context)
  }

  private go(context: InstanceContext) {
    this.asking = false
    //Navegar para a rota em que já se está é rejeição esperada do vue-router, não erro — o
    //mesmo caso das abas de coleção
    this.$router.push(instanceRoute(context)).catch(() => undefined)
  }
}

export default toNative(GlobalSearch)
</script>

<style scoped>
/* A palavra em cima e o atalho embaixo, alinhados pela esquerda — a mesma pilha do logo com o selo
   do modo, que existiu até os modos saírem. As linhas apertadas para as duas caberem na altura do
   botão. */
.search-trigger-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
}

/* O atalho como tecla desenhada, e não como texto solto: é o idioma que todo mundo reconhece
   de outras paletas. Translúcido sobre a barra colorida, que é escura.

   A mesma tecla de quando ficava ao lado da palavra (pedido do usuário), só menor — letra de 9px e
   linha de 11px, em vez de 10 e 14 —, para caber embaixo dela na altura do botão. */
.search-trigger-keys {
  margin-top: 2px;
  padding: 0 4px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 3px;
  font-size: 9px;
  line-height: 11px;
  opacity: 0.8;
}

/* Coluna com o campo preso em cima, a lista rolando no meio e as teclas embaixo. */
.search-palette {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* O teto de altura é só do desktop. Abaixo do md o diálogo é fullscreen — é o mesmo corte do
   `smAndDown` no template —, e ali um teto de 70vh deixaria um cartão de três quartos de tela
   flutuando dentro de uma tela inteira. Em cima, a paleta cresce com o que achou até o teto,
   que é o que impede a lista de encostar nas bordas da janela. */
@media (min-width: 960px) {
  .search-palette {
    height: auto;
    max-height: min(70vh, 640px);
  }
}

.search-field {
  padding: 4px 8px;
  flex: 0 0 auto;
}

.search-results {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

/* O estado vazio e a dica ocupam o lugar da lista sem fingir que são uma: texto centrado,
   discreto, e a mesma altura confortável de uma linha e meia. */
.search-note {
  padding: 28px 16px;
  text-align: center;
  font-size: 13px;
  opacity: 0.65;
}

/* O cabeçalho do grupo é uma linha só: o nome à esquerda, a contagem à direita. */
.search-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 1;
}

.search-group-name {
  font-weight: 600;
}

.search-group-count {
  font-weight: 400;
  opacity: 0.6;
  letter-spacing: normal;
  text-transform: none;
}

.search-title {
  display: flex;
  align-items: center;
  min-width: 0;
}

/* A largura que sobra, encolhível: o nome corta com reticências dentro do próprio
   `ColoredValue`, e é o `min-width: 0` que permite ao item flex encolher abaixo do conteúdo —
   sem ele, um identificador longo empurraria a situação fora da linha. */
.search-identifier {
  flex: 1 1 auto;
  min-width: 0;
}

.search-state {
  margin-inline-start: 8px;
  flex: 0 0 auto;
}

/* A segunda linha é informação de apoio: menor, mais apagada, e também numa linha só. */
.search-why {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-top: 1px;
  font-size: 12px;
  opacity: 0.7;
}

.search-why-icon {
  margin-inline-end: 4px;
  flex: 0 0 auto;
}

.search-why-field {
  flex: 0 0 auto;
}

.search-why-sep {
  margin-inline: 5px;
  flex: 0 0 auto;
}

.search-why-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* O realce é do texto, não uma caixa em volta dele — o mesmo argumento do chip de opção. Sem
   cor de fundo do navegador, que é amarelo-marcador e destoa de tudo. */
.search-why-text mark {
  background: transparent;
  color: inherit;
  font-weight: 700;
}

.search-hints {
  flex: 0 0 auto;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 6px 12px;
  font-size: 11px;
  opacity: 0.6;
}
</style>
